// EmsSupply — Station-level EMS supply readiness dashboard
// Sections: KPI row, stock heatmap, reorder list, warehouse balance, station comparison chart

import { useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { useEmsStock, useEmsWarehouse } from "../hooks/useOps";
import TruncationNotice from "../components/shared/TruncationNotice";
import {
  rowWindowState,
  rowWindowTileValue,
  rowWindowAccent,
  isRowWindowSuspect,
} from "../lib/rowWindow";
import type { EmsStockRow, EmsWarehouseRow } from "../hooks/useOps";
import { useAggregates } from "../hooks/useDashboard";
import KPICard from "../components/dashboard/KPICard";
import ChartCard from "../components/dashboard/ChartCard";
import StatusBadge from "../components/dashboard/StatusBadge";
import StockHeatmap from "../components/dashboard/StockHeatmap";
import type { StockCell } from "../components/dashboard/StockHeatmap";
import { useTheme } from "../hooks/useTheme";
import DateRangeToggle, {
  useSinceDate,
  isAfterSince,
} from "../components/dashboard/DateRangeToggle";
import type { DateRange } from "../components/dashboard/DateRangeToggle";

// ── Helpers ───────────────────────────────────────────────────────────────────

function asNumber(v: unknown, fallback = 0): number {
  if (typeof v === "number") return v;
  if (typeof v === "string") {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : fallback;
  }
  return fallback;
}

function complianceAccent(pct: number): "green" | "amber" | "red" {
  if (pct >= 95) return "green";
  if (pct >= 80) return "amber";
  return "red";
}

function stationBarColor(rate: number): string {
  if (rate >= 95) return "#16a34a"; // green-600
  if (rate >= 80) return "#d97706"; // amber-600
  return "#dc2626"; // red-600
}

// ── Sub-components ─────────────────────────────────────────────────────────────

// Reorder list — items where current < max, sorted by shortfall descending
interface ReorderRow {
  product: string;
  station: string;
  current: number;
  target: number;
  shortfall: number;
}

function ReorderList({
  rows,
  isLoading,
  partial,
}: {
  rows: ReorderRow[];
  isLoading: boolean;
  /** True when the underlying stock rows are truncated or unverifiable. */
  partial?: boolean;
}) {
  if (isLoading) {
    return (
      <div className="overflow-x-auto">
        <table className="w-full min-w-[520px] text-left">
          <thead>
            <tr className="border-b border-theme-subtle">
              {["Product", "Station", "Current", "Target", "Shortfall"].map(
                (h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-xs font-semibold text-theme-muted uppercase tracking-wider"
                  >
                    {h}
                  </th>
                ),
              )}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 6 }).map((_, i) => (
              <tr key={i} className="border-b border-theme-subtle">
                {Array.from({ length: 5 }).map((__, j) => (
                  <td key={j} className="px-4 py-3">
                    <div
                      className="skeleton h-4 rounded-md"
                      style={{ width: `${40 + ((i * 11 + j * 13) % 60)}px` }}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (rows.length === 0) {
    // An empty reorder list over a truncated read is not an all-clear — the
    // shortfalls may simply be in the rows that were never loaded. Say that
    // instead of printing green.
    if (partial) {
      return (
        <div className="flex flex-col items-center justify-center h-28 gap-2">
          <p className="text-sm text-status-amber font-medium">
            No shortfalls in the rows loaded
          </p>
          <p className="text-xs text-theme-muted opacity-70">
            Stock data is incomplete — this is not an all-clear. See the notice
            at the top of the page.
          </p>
        </div>
      );
    }
    return (
      <div className="flex flex-col items-center justify-center h-28 gap-2">
        <p className="text-sm text-status-green font-medium">
          All items fully stocked
        </p>
        <p className="text-xs text-theme-muted opacity-70">
          No items are currently below target levels
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[520px] text-left">
        <thead>
          <tr className="border-b border-theme-subtle">
            <th className="px-4 py-3 text-xs font-semibold text-theme-muted uppercase tracking-wider">
              Product
            </th>
            <th className="px-4 py-3 text-xs font-semibold text-theme-muted uppercase tracking-wider">
              Station
            </th>
            <th className="px-4 py-3 text-xs font-semibold text-theme-muted uppercase tracking-wider text-right">
              Current
            </th>
            <th className="px-4 py-3 text-xs font-semibold text-theme-muted uppercase tracking-wider text-right">
              Target
            </th>
            <th className="px-4 py-3 text-xs font-semibold text-theme-muted uppercase tracking-wider text-right">
              Shortfall
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={`${row.station}-${row.product}`}
              className="hover:bg-theme-overlay/50 transition-colors"
            >
              <td
                className="px-4 py-3 text-sm text-theme-primary font-medium max-w-[220px] truncate"
                title={row.product}
              >
                {row.product}
              </td>
              <td className="px-4 py-3 text-sm text-theme-secondary">
                {row.station}
              </td>
              <td className="px-4 py-3 text-sm text-right tabular-nums text-theme-primary">
                {row.current}
              </td>
              <td className="px-4 py-3 text-sm text-right tabular-nums text-theme-secondary">
                {row.target}
              </td>
              <td className="px-4 py-3 text-sm text-right tabular-nums">
                <span className="font-bold text-status-red">
                  -{row.shortfall}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Warehouse balance table
function WarehouseTable({
  rows,
  isLoading,
}: {
  rows: EmsWarehouseRow[];
  isLoading: boolean;
}) {
  if (isLoading) {
    return (
      <div className="overflow-x-auto">
        <table className="w-full min-w-[320px] text-left">
          <thead>
            <tr className="border-b border-theme-subtle">
              {["Product", "Category", "Warehouse Stock"].map((h) => (
                <th
                  key={h}
                  className="px-4 py-3 text-xs font-semibold text-theme-muted uppercase tracking-wider"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 5 }).map((_, i) => (
              <tr key={i} className="border-b border-theme-subtle">
                {Array.from({ length: 3 }).map((__, j) => (
                  <td key={j} className="px-4 py-3">
                    <div
                      className="skeleton h-4 rounded-md"
                      style={{ width: `${40 + ((i * 13 + j * 17) % 70)}px` }}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-28 gap-2 text-center">
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-theme-muted opacity-50"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M12 16v-4M12 8h.01" />
        </svg>
        <p className="text-sm text-theme-muted">Awaiting data sync</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[320px] text-left">
        <thead>
          <tr className="border-b border-theme-subtle">
            <th className="px-4 py-3 text-xs font-semibold text-theme-muted uppercase tracking-wider">
              Product
            </th>
            <th className="px-4 py-3 text-xs font-semibold text-theme-muted uppercase tracking-wider">
              Category
            </th>
            <th className="px-4 py-3 text-xs font-semibold text-theme-muted uppercase tracking-wider text-right">
              Warehouse Stock
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => {
            const qty = asNumber(row.quantity_on_hand);
            const isZero = qty === 0;
            return (
              <tr
                key={row.id ?? `${row.item_name}-${idx}`}
                className={`hover:bg-theme-overlay/50 transition-colors ${isZero ? "bg-red-500/5" : ""}`}
              >
                <td
                  className={`px-4 py-3 text-sm font-medium max-w-[240px] truncate ${isZero ? "text-status-red" : "text-theme-primary"}`}
                  title={row.item_name ?? ""}
                >
                  {row.item_name ?? "—"}
                </td>
                <td className="px-4 py-3 text-sm text-theme-secondary">
                  {row.category ?? "—"}
                </td>
                <td className="px-4 py-3 text-sm text-right tabular-nums">
                  {isZero ? (
                    <span className="font-bold text-status-red">0</span>
                  ) : (
                    <span className="font-medium text-theme-primary">
                      {qty.toLocaleString()}
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Station compliance bar chart
interface StationComplianceRow {
  station: string;
  rate: number;
  compliant: number;
  total: number;
}

interface StationTooltipPayload {
  payload?: StationComplianceRow;
}

function StationTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: StationTooltipPayload[];
}) {
  if (!active || !payload || payload.length === 0) return null;
  const d = payload[0]?.payload;
  if (!d) return null;
  return (
    <div className="glass rounded-lg px-3 py-2.5 text-xs shadow-lg min-w-[140px]">
      <p className="font-semibold text-theme-primary mb-1.5">{d.station}</p>
      <div className="flex justify-between gap-4">
        <span className="text-theme-muted">Compliance</span>
        <span className="font-bold" style={{ color: stationBarColor(d.rate) }}>
          {d.rate.toFixed(1)}%
        </span>
      </div>
      <div className="flex justify-between gap-4 mt-0.5">
        <span className="text-theme-muted">Stocked</span>
        <span className="text-theme-secondary">
          {d.compliant}/{d.total}
        </span>
      </div>
    </div>
  );
}

function StationComplianceChart({
  data,
  isLoading,
  isDark,
}: {
  data: StationComplianceRow[];
  isLoading: boolean;
  isDark: boolean;
}) {
  const axisColor = isDark ? "#9ca3af" : "#6b7280";
  const gridColor = isDark ? "#27272a" : "#e5e7eb";

  if (isLoading) {
    return (
      <div className="flex items-end gap-3 h-48 px-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <div
            key={i}
            className="flex-1 skeleton rounded-t-sm"
            style={{ height: `${40 + ((i * 19 + 31) % 55)}%` }}
          />
        ))}
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-48 gap-2 text-center">
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-theme-muted opacity-50"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M12 16v-4M12 8h.01" />
        </svg>
        <p className="text-sm text-theme-muted">Awaiting data sync</p>
        <p className="text-xs text-theme-muted opacity-60">
          Station compliance will appear after stock data syncs
        </p>
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
        <CartesianGrid
          vertical={false}
          stroke={gridColor}
          strokeDasharray="3 3"
        />
        <XAxis
          dataKey="station"
          tick={{ fontSize: 11, fill: axisColor }}
          tickLine={false}
          axisLine={false}
        />
        <YAxis
          domain={[0, 100]}
          tick={{ fontSize: 11, fill: axisColor }}
          tickLine={false}
          axisLine={false}
          tickFormatter={(v) => `${v}%`}
          width={40}
        />
        <Tooltip
          content={<StationTooltip />}
          cursor={{
            fill: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)",
          }}
        />
        <Bar
          dataKey="rate"
          name="Compliance %"
          radius={[3, 3, 0, 0]}
          maxBarSize={56}
        >
          {data.map((entry) => (
            <Cell
              key={entry.station}
              fill={stationBarColor(entry.rate)}
              fillOpacity={0.85}
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function EmsSupply() {
  const INTERVAL = 120_000;

  const [range, setRange] = useState<DateRange>("all");
  const sinceDate = useSinceDate(range);

  const {
    data: stockData,
    isLoading: stockLoading,
    lastUpdatedAt,
  } = useEmsStock(undefined, INTERVAL);
  const { data: warehouseData, isLoading: warehouseLoading } =
    useEmsWarehouse(INTERVAL);
  const { data: aggregatesData, isLoading: aggLoading } =
    useAggregates(INTERVAL);
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  const allStockRows: EmsStockRow[] = stockData?.data ?? [];
  const warehouseRows: EmsWarehouseRow[] = warehouseData?.data ?? [];

  // ── Row completeness ──────────────────────────────────────────────────────
  // Everything below this line is computed over the rows the API actually
  // returned. Until 2026-08-27 that was 500 of 880 EMS stock rows and the page
  // said nothing about it, so "Items Below Target" was a confident number over
  // 57% of the department's supplies. These windows drive the banner AND the
  // tiles: a count over a partial set renders as a floor ("12+"), never as a
  // total.
  const stockWindow = rowWindowState(stockData);
  const warehouseWindow = rowWindowState(warehouseData);
  const stockPartial = isRowWindowSuspect(stockWindow);

  // ── Date range filter: filter by last_checked_date (session date) ────────
  const stockRows = useMemo<EmsStockRow[]>(() => {
    if (!sinceDate) return allStockRows;
    return allStockRows.filter((r) =>
      isAfterSince(r.last_checked_date ?? "", sinceDate),
    );
  }, [allStockRows, sinceDate]);
  const agg = aggregatesData?.aggregates ?? {};

  // ── Derived values ────────────────────────────────────────────────────────

  const emsComplianceRaw = agg["ems_stock_compliance"];
  const emsComplianceExists =
    emsComplianceRaw !== undefined && emsComplianceRaw !== null;
  const emsComplianceFraction = asNumber(emsComplianceRaw);
  const emsCompliancePct =
    emsComplianceFraction > 0 && emsComplianceFraction <= 1
      ? emsComplianceFraction * 100
      : emsComplianceFraction > 1
        ? emsComplianceFraction
        : emsComplianceExists
          ? 0
          : -1; // -1 signals "key absent" (no data)

  // Items below target (current < reorder_level used as target proxy)
  const itemsBelowTarget = useMemo(
    () =>
      stockRows.filter((r) => {
        const current = asNumber(r.quantity_on_hand);
        const target = asNumber(r.reorder_level);
        return target > 0 && current < target;
      }).length,
    [stockRows],
  );

  // Unique stations
  const stations = useMemo(
    () =>
      Array.from(
        new Set(stockRows.map((r) => r.station ?? "").filter(Boolean)),
      ).sort(),
    [stockRows],
  );

  // Unique products
  const products = useMemo(
    () =>
      Array.from(
        new Set(stockRows.map((r) => r.item_name ?? "").filter(Boolean)),
      ).sort(),
    [stockRows],
  );

  // Warehouse items count
  const warehouseItemCount = warehouseRows.length;

  // Heatmap cells
  const heatmapCells = useMemo<StockCell[]>(
    () =>
      stockRows
        .filter((r) => r.station && r.item_name)
        .map((r) => ({
          station: r.station!,
          product: r.item_name!,
          current: asNumber(r.quantity_on_hand),
          target: asNumber(r.reorder_level),
        })),
    [stockRows],
  );

  // Reorder list — below target, sorted by shortfall descending
  const reorderRows = useMemo<ReorderRow[]>(
    () =>
      stockRows
        .filter((r) => {
          const current = asNumber(r.quantity_on_hand);
          const target = asNumber(r.reorder_level);
          return target > 0 && current < target;
        })
        .map((r) => ({
          product: r.item_name ?? "—",
          station: r.station ?? "—",
          current: asNumber(r.quantity_on_hand),
          target: asNumber(r.reorder_level),
          shortfall: asNumber(r.reorder_level) - asNumber(r.quantity_on_hand),
        }))
        .sort((a, b) => b.shortfall - a.shortfall),
    [stockRows],
  );

  // Station compliance data
  const stationComplianceData = useMemo<StationComplianceRow[]>(() => {
    if (stations.length === 0) return [];
    return stations.map((station) => {
      // Match the headline KPI aggregate's definition: only rows with a real
      // target (stock_max > 0) are scored. Previously rows with no target were
      // counted as compliant, inflating station rates and diverging from the KPI.
      const stationRows = stockRows.filter(
        (r) => r.station === station && asNumber(r.reorder_level) > 0,
      );
      const total = stationRows.length;
      const compliant = stationRows.filter((r) => {
        const current = asNumber(r.quantity_on_hand);
        const target = asNumber(r.reorder_level);
        return current >= target;
      }).length;
      const rate = total > 0 ? (compliant / total) * 100 : 0;
      return { station, rate, compliant, total };
    });
  }, [stations, stockRows]);

  // KPI loading state
  const isLoading = stockLoading || aggLoading;

  // Refresh indicator
  const sec = lastUpdatedAt
    ? Math.round((Date.now() - lastUpdatedAt.getTime()) / 1000)
    : null;

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto space-y-6 md:space-y-8 overflow-x-hidden">
      {/* ── Page header ──────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-theme-primary">
            EMS Supply Status
          </h1>
          <p className="text-theme-secondary mt-1 text-sm">
            Station-level supply readiness — stock compliance, reorder needs,
            and warehouse balance.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <StatusBadge label="Live" variant="green" />
          {sec !== null && (
            <div className="flex items-center gap-1.5 text-xs text-theme-muted">
              <span className="w-1.5 h-1.5 rounded-full bg-status-green shrink-0" />
              <span>Updated {sec < 5 ? "just now" : `${sec}s ago`}</span>
            </div>
          )}
        </div>
      </div>

      {/* ── Row-completeness notices ──────────────────────────────────── */}
      {/* Above the fold, above the KPI row: if the page is rendering part of a
          table, that is the first thing a reader needs to know. */}
      <TruncationNotice
        response={stockData}
        label="EMS stock"
        // Same rule as the IRONS notice: no station control exists on this
        // page, so do not instruct one. State the consequence instead.
        hint="Every count and chart on this page is computed over the rows shown, so treat them as a floor. Station coverage may be partial."
      />
      <TruncationNotice response={warehouseData} label="EMS warehouse" />

      {/* ── Date Range Toggle ─────────────────────────────────────────── */}
      <DateRangeToggle value={range} onChange={setRange} />

      {/* ── KPI Row ───────────────────────────────────────────────────── */}
      <section>
        <h2 className="text-xs font-semibold text-theme-muted uppercase tracking-wider mb-4">
          Supply Overview
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {/* KPI 1: Stock Compliance % */}
          <KPICard
            label="Stock Compliance"
            value={
              aggLoading
                ? "—"
                : emsCompliancePct < 0
                  ? "No data"
                  : `${emsCompliancePct.toFixed(1)}%`
            }
            subtitle="target: ≥95% stocked"
            accent={
              aggLoading || emsCompliancePct < 0
                ? "neutral"
                : complianceAccent(emsCompliancePct)
            }
            isLoading={aggLoading}
            trend={
              !aggLoading && emsCompliancePct >= 0
                ? {
                    direction: emsCompliancePct >= 95 ? "up" : "down",
                    label:
                      emsCompliancePct >= 95 ? "On target" : "Below target",
                    positiveIsGood: true,
                  }
                : undefined
            }
          />

          {/* KPI 2: Items Below Target */}
          <KPICard
            label="Items Below Target"
            value={
              stockLoading
                ? "—"
                : rowWindowTileValue(itemsBelowTarget, stockWindow)
            }
            subtitle={
              stockPartial
                ? "counted over the rows loaded — see the notice above"
                : itemsBelowTarget === 0
                  ? "All stations stocked"
                  : "need restocking across stations"
            }
            accent={
              stockLoading
                ? "neutral"
                : rowWindowAccent(
                    stockWindow,
                    itemsBelowTarget === 0
                      ? "green"
                      : itemsBelowTarget > 10
                        ? "red"
                        : "amber",
                  )
            }
            isLoading={stockLoading}
            trend={
              // No trend over a partial set: "All stocked" across 500 of 880
              // rows is the exact false all-clear this page shipped.
              !stockLoading && !stockPartial
                ? {
                    direction: itemsBelowTarget === 0 ? "down" : "up",
                    label:
                      itemsBelowTarget === 0
                        ? "All stocked"
                        : `${itemsBelowTarget} items low`,
                    positiveIsGood: false,
                  }
                : undefined
            }
          />

          {/* KPI 3: Stations Counted */}
          <KPICard
            label="Stations Counted"
            value={
              stockLoading
                ? "—"
                : rowWindowTileValue(stations.length, stockWindow)
            }
            subtitle={
              stockPartial
                ? `${products.length} products seen in the rows loaded`
                : `${products.length} products tracked`
            }
            accent={rowWindowAccent(stockWindow, "neutral")}
            isLoading={stockLoading}
          />

          {/* KPI 4: Warehouse Items */}
          <KPICard
            label="Warehouse Items"
            value={
              warehouseLoading
                ? "—"
                : rowWindowTileValue(warehouseItemCount, warehouseWindow)
            }
            subtitle={
              !warehouseLoading &&
              warehouseRows.filter((r) => asNumber(r.quantity_on_hand) === 0)
                .length > 0
                ? `${warehouseRows.filter((r) => asNumber(r.quantity_on_hand) === 0).length} zero-stock items`
                : "central warehouse inventory"
            }
            accent={
              warehouseLoading
                ? "neutral"
                : rowWindowAccent(
                    warehouseWindow,
                    warehouseRows.filter(
                      (r) => asNumber(r.quantity_on_hand) === 0,
                    ).length > 0
                      ? "amber"
                      : "green",
                  )
            }
            isLoading={warehouseLoading}
            trend={
              !warehouseLoading &&
              warehouseItemCount > 0 &&
              !isRowWindowSuspect(warehouseWindow)
                ? {
                    direction:
                      warehouseRows.filter(
                        (r) => asNumber(r.quantity_on_hand) === 0,
                      ).length === 0
                        ? "up"
                        : "down",
                    label:
                      warehouseRows.filter(
                        (r) => asNumber(r.quantity_on_hand) === 0,
                      ).length === 0
                        ? "Fully stocked"
                        : "Some zero-stock",
                    positiveIsGood: true,
                  }
                : undefined
            }
          />
        </div>
      </section>

      {/* ── Section 1: Stock Level Heatmap ──────────────────────────── */}
      <section>
        <ChartCard
          title="Stock Level Heatmap"
          subtitle="Station columns × product rows — color by stock vs reorder level ratio"
          headerRight={
            stockLoading ? (
              <span className="text-xs text-theme-muted animate-pulse">
                Loading...
              </span>
            ) : stockRows.length === 0 ? (
              <StatusBadge label="Sync pending" variant="yellow" />
            ) : (
              <StatusBadge
                label={`${stockRows.length} records`}
                variant="muted"
              />
            )
          }
          minHeight={
            products.length > 0 ? Math.max(200, products.length * 40 + 80) : 200
          }
        >
          <StockHeatmap
            cells={heatmapCells}
            stations={stations}
            products={products}
            isLoading={stockLoading}
          />
        </ChartCard>
      </section>

      {/* ── Section 2: Reorder List ──────────────────────────────────── */}
      <section>
        <ChartCard
          title="Reorder List"
          subtitle="Items below target reorder level — sorted by shortfall, largest first"
          headerRight={
            stockLoading ? (
              <span className="text-xs text-theme-muted animate-pulse">
                Loading...
              </span>
            ) : reorderRows.length > 0 ? (
              <StatusBadge
                label={`${reorderRows.length} items`}
                variant="red"
              />
            ) : (
              <StatusBadge label="All stocked" variant="green" />
            )
          }
          minHeight={0}
        >
          <ReorderList
            rows={reorderRows}
            isLoading={stockLoading}
            partial={stockPartial}
          />
        </ChartCard>
      </section>

      {/* ── Section 3: Warehouse Balance ────────────────────────────── */}
      <section>
        <ChartCard
          title="Warehouse Balance"
          subtitle="Central warehouse stock levels — zero-stock items highlighted in red"
          headerRight={
            warehouseLoading ? (
              <span className="text-xs text-theme-muted animate-pulse">
                Loading...
              </span>
            ) : warehouseRows.filter((r) => asNumber(r.quantity_on_hand) === 0)
                .length > 0 ? (
              <StatusBadge
                label={`${warehouseRows.filter((r) => asNumber(r.quantity_on_hand) === 0).length} zero-stock`}
                variant="red"
              />
            ) : warehouseRows.length > 0 ? (
              <StatusBadge
                label={`${warehouseRows.length} products`}
                variant="muted"
              />
            ) : null
          }
          minHeight={0}
        >
          <WarehouseTable rows={warehouseRows} isLoading={warehouseLoading} />
        </ChartCard>
      </section>

      {/* ── Section 4: Station Comparison ───────────────────────────── */}
      <section>
        <ChartCard
          title="Station Compliance Comparison"
          subtitle="Per-station compliance rate — % of items at or above reorder level"
          headerRight={
            isLoading ? (
              <span className="text-xs text-theme-muted animate-pulse">
                Loading...
              </span>
            ) : stationComplianceData.length > 0 ? (
              <StatusBadge
                label={`${stations.length} stations`}
                variant="blue"
              />
            ) : null
          }
          minHeight={240}
        >
          <StationComplianceChart
            data={stationComplianceData}
            isLoading={stockLoading}
            isDark={isDark}
          />
        </ChartCard>
      </section>
    </div>
  );
}
